import { useEffect } from 'react';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { aboutInfo, faqList } from '@/mocks/about';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const breadcrumbItems = [
    { '@type': 'ListItem' as const, position: 1, name: '首页', item: 'https://www.90yingshi.com' },
    { '@type': 'ListItem' as const, position: 2, name: '关于我们', item: 'https://www.90yingshi.com/about' },
  ];

  const faqQuestions = faqList.map((item, idx) => ({
    '@type': 'Question' as const,
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer' as const,
      text: item.answer,
    },
  }));

  return (
    <>
      <SEOHead
        title="关于我们 - 品牌介绍与联系方式"
        description="90影视 - 免费在线观看不缓冲高清影视平台。了解我们的品牌故事、核心优势、用户口碑及联系方式。电影电视剧综艺动漫全集高清无广告。"
        keywords="90影视,关于我们,联系方式,免费影视,高清在线观看,品牌介绍"
        canonicalUrl="https://www.90yingshi.com/about"
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 | 90影视',
          description: '90影视品牌介绍与联系方式',
          url: 'https://www.90yingshi.com/about',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems,
          },
        }}
      />
      <SchemaOrg type="BreadcrumbList" data={{ items: breadcrumbItems }} />
      <SchemaOrg type="FAQPage" data={{ questions: faqQuestions }} />

      <main className="min-h-screen pt-16">
        <section className="relative w-full bg-background-100 border-b border-background-200/70 py-16 md:py-24">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-50 font-heading mb-4">
              关于 <span className="text-primary-400">90影视</span>
            </h1>
            <p className="text-foreground-400 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.brandIntro}
            </p>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading mb-6">我们的故事</h2>
                <p className="text-foreground-400 text-sm leading-relaxed mb-4">{aboutInfo.brandStory}</p>
                <p className="text-foreground-400 text-sm leading-relaxed">
                  从最初的几个人到如今的专业团队，我们始终保持着对影视内容的热爱。我们精心筛选每一部作品，确保每一个用户都能在90影视找到心仪的影视内容。未来，我们将继续努力，为全球华语用户提供更加优质的服务。
                </p>
              </div>
              <div className="rounded-xl overflow-hidden bg-background-100 border border-background-200/70">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20with%20warm%20lighting%20and%20creative%20team%20collaborating%2C%20cozy%20atmosphere%2C%20wooden%20desk%2C%20large%20monitors%2C%20indoor%20plants%2C%20editorial%20photography%20with%20soft%20shadows&width=800&height=500&seq=about-story-img&orientation=landscape"
                  alt="90影视团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading mb-3">为什么选择90影视</h2>
              <p className="text-foreground-500 text-sm">六大核心优势，打造极致观影体验</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {aboutInfo.features.map((feature) => (
                <div
                  key={feature.title}
                  className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-7 hover:border-primary-500/20 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent-500/10 flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-accent-400 text-xl`}></i>
                  </div>
                  <h3 className="text-foreground-200 font-semibold text-base font-heading mb-2">{feature.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading mb-3">用户口碑</h2>
              <p className="text-foreground-500 text-sm">来自真实用户的高度评价</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {aboutInfo.testimonials.map((t) => (
                <div
                  key={t.name}
                  className="bg-background-100 border border-background-200/70 rounded-xl p-6 md:p-7"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-400 text-sm leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                      <span className="text-primary-400 text-xs font-bold">{t.name.charAt(0)}</span>
                    </div>
                    <span className="text-foreground-300 text-sm font-medium">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading mb-3">常见问题</h2>
              <p className="text-foreground-500 text-sm">关于90影视，你想知道的都在这里</p>
            </div>

            <div className="max-w-3xl mx-auto space-y-3">
              {faqList.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 md:p-6 cursor-pointer list-none">
                    <span className="text-foreground-200 font-medium text-sm pr-4">{faq.question}</span>
                    <span className="flex-shrink-0 text-foreground-500 group-open:rotate-180 transition-transform">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-5 md:px-6 pb-5 md:pb-6">
                    <p className="text-foreground-500 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading mb-3">联系我们</h2>
              <p className="text-foreground-500 text-sm">有任何问题或建议？欢迎联系我们</p>
            </div>

            <div className="max-w-2xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-400 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-foreground-600 text-xs mb-0.5">电子邮箱</div>
                    <div className="text-foreground-200 text-sm font-medium">{aboutInfo.contact.email}</div>
                  </div>
                </div>
                <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-primary-400 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-foreground-600 text-xs mb-0.5">微信公众号</div>
                    <div className="text-foreground-200 text-sm font-medium">{aboutInfo.contact.wechat}</div>
                  </div>
                </div>
                <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-qq-line text-primary-400 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-foreground-600 text-xs mb-0.5">QQ群</div>
                    <div className="text-foreground-200 text-sm font-medium">{aboutInfo.contact.qq}</div>
                  </div>
                </div>
                <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-400 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-foreground-600 text-xs mb-0.5">工作时间</div>
                    <div className="text-foreground-200 text-sm font-medium">{aboutInfo.contact.workTime}</div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 mt-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-primary-400 text-lg"></i>
                </div>
                <div>
                  <div className="text-foreground-600 text-xs mb-0.5">公司地址</div>
                  <div className="text-foreground-200 text-sm font-medium">{aboutInfo.contact.address}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}